/**
 * The caller's own notifications: the bell's rows and the mark-read handoff.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Schema truth: NotificationList {total, unread, items[NotificationOut]} and
 * MarkReadResponse {id, read_at} (frontend/src/api/schema.d.ts, M5 P2). Marking another
 * person's notification read is a 404 by server rule; the row comes back read so the
 * client renders the truth rather than assuming it.
 */

import { apiJson, num, optStr, str } from "./client";

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

export interface Notification {
  id: string;
  kind: string;
  message: string;
  deepLink: string | null;
  createdAt: string;
  readAt: string | null;
  /**
   * Wave W2 (Mahmoud's UAT, 2026-10-10): the task this handoff announced, if any.
   * `taskOpen`: the act still waits. `attendedAt`: the task is complete, so the
   * notification has been dealt with and leaves the unread badge. Both are the server's
   * reading of the task itself; the client never decides them.
   */
  taskId: string | null;
  taskOpen: boolean;
  attendedAt: string | null;
}

export interface NotificationList {
  total: number;
  /** What the badge shows: unread and not attended. */
  unread: number;
  /** How many rows are attended (their task is complete). */
  attended: number;
  items: Notification[];
}

function notification(body: Record<string, unknown>): Notification {
  return {
    id: str(body.id),
    kind: str(body.kind),
    message: str(body.message),
    deepLink: optStr(body.deep_link),
    createdAt: str(body.created_at),
    readAt: optStr(body.read_at),
    taskId: optStr(body.task_id),
    taskOpen: body.task_open === true,
    attendedAt: optStr(body.attended_at),
  };
}

function list(body: Record<string, unknown>): NotificationList {
  const items = Array.isArray(body.items) ? body.items : [];
  return {
    total: num(body.total),
    unread: num(body.unread),
    attended: typeof body.attended === "number" ? body.attended : 0,
    items: items.map((entry) => notification(entry as Record<string, unknown>)),
  };
}

export async function listNotifications(
  token: string,
  filters: {
    unreadOnly?: boolean;
    limit?: number;
    signal?: AbortSignal | undefined;
  } = {},
): Promise<NotificationList> {
  const params = new URLSearchParams();
  if (filters.unreadOnly === true) {
    params.set("unread_only", "true");
  }
  if (filters.limit !== undefined) {
    params.set("limit", String(filters.limit));
  }
  const suffix = params.size > 0 ? `?${params.toString()}` : "";
  return apiJson(token, `/notifications${suffix}`, (raw) => list(assertRecord(raw)), {
    signal: filters.signal,
  });
}

export async function markNotificationRead(
  token: string,
  notificationId: string,
): Promise<{ id: string; readAt: string | null }> {
  return apiJson(
    token,
    `/notifications/${notificationId}/read`,
    (raw) => {
      if (typeof raw !== "object" || raw === null) {
        throw new Error("Expected an object body");
      }
      const body = raw as Record<string, unknown>;
      return { id: str(body.id), readAt: optStr(body.read_at) };
    },
    { method: "POST" },
  );
}

/** Whether a notification is attended: the task it announced is complete. */
export function isAttended(entry: Notification): boolean {
  return entry.attendedAt !== null;
}

/**
 * Whether a notification counts as unread: never read AND not attended. A handoff
 * whose task was done (from My tasks, or the record page) has been dealt with, clicked
 * or not (wave W2), so it never sits in the badge.
 */
export function isUnread(entry: Notification): boolean {
  return entry.readAt === null && !isAttended(entry);
}
